import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useStudentAuth } from "@/frontend/contexts/StudentAuthContext";
import { useCurrentStudent } from "@/backend/hooks/useStudent";
import {
  useRegulationAcceptanceCheck,
  useRecordRegulationAcceptance,
} from "@/backend/hooks/useStudentRegulation";
import { RegulationAcceptanceDialog } from "@/frontend/components/student/RegulationAcceptanceDialog";
import { PasswordChangeDialog } from "@/frontend/components/student/PasswordChangeDialog";
import { CompleteDetailsDialog } from "@/frontend/components/student/CompleteDetailsDialog";
import { useChangePassword } from "@/backend/hooks/usePasswordChange";
import { useMyGuestDetails } from "@/backend/hooks/useMyGuestDetails";
import { useUpdateMyGuestDetails } from "@/backend/hooks/useUpdateMyGuestDetails";
import { isGuestInfoComplete } from "@/shared/constants/guestForm";
import { Loader2 } from "lucide-react";
import { useToast } from "@/backend/hooks/use-toast";
import type { GuestInfo } from "@/shared/types";

interface StudentPortalGuardProps {
  children: ReactNode;
}

export const StudentPortalGuard = ({ children }: StudentPortalGuardProps) => {
  const { user, isStudent, isLoading: authLoading } = useStudentAuth();
  const { toast } = useToast();

  // Fetch student data
  const { data: student, isLoading: studentLoading } = useCurrentStudent(user?.uid);

  // Check regulation acceptance
  const {
    data: acceptanceCheck,
    isLoading: acceptanceLoading,
    error: acceptanceError,
  } = useRegulationAcceptanceCheck(user?.uid, student?.residenceId);

  // Mutation for recording acceptance
  const recordAcceptance = useRecordRegulationAcceptance();

  // Mutation for password change
  const changePassword = useChangePassword();

  // The student's own guest entry on their booking, to spot the personal
  // details that bookings predating the DEV2-1038 write-path fix never stored.
  const { data: myGuestDetails, isLoading: guestDetailsLoading } = useMyGuestDetails(
    user?.uid,
    student?.bookingId,
    student?.email
  );
  const updateMyGuestDetails = useUpdateMyGuestDetails();

  // Loading state
  if (authLoading || studentLoading || acceptanceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/student/login" replace />;
  }

  // Not a student role - redirect to login
  if (!isStudent) {
    return <Navigate to="/student/login" replace />;
  }

  // Student data not found - error state
  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-2">Account Error</h2>
          <p className="text-muted-foreground">
            Student account data not found. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Password change required - show blocking dialog
  if (student.needsPasswordChange) {
    const handlePasswordChange = async (
      currentPassword: string,
      newPassword: string
    ) => {
      try {
        await changePassword.mutateAsync({
          studentId: user.uid,
          currentPassword,
          newPassword,
        });

        toast({
          title: "Password Changed",
          description: "Your password has been updated successfully!",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to change password",
          variant: "destructive",
        });
        throw error; // Re-throw so dialog can handle it
      }
    };

    return (
      <PasswordChangeDialog
        studentId={user.uid}
        studentEmail={student.email}
        onPasswordChange={handlePasswordChange}
        isChanging={changePassword.isPending}
      />
    );
  }

  // Error state - show if there's an acceptance check error
  if (acceptanceError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-destructive mb-2">Error Loading Regulations</h2>
          <p className="text-muted-foreground mb-4">
            {acceptanceError instanceof Error ? acceptanceError.message : 'Unknown error occurred'}
          </p>
          <p className="text-sm text-muted-foreground">
            Residence ID: {student?.residenceId || 'N/A'}
          </p>
        </div>
      </div>
    );
  }

  // No active regulation - error state (shouldn't happen)
  if (!acceptanceCheck?.regulation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-2">No Active Regulations</h2>
          <p className="text-muted-foreground mb-4">
            No active regulations found for your residence. Please contact support.
          </p>
          <div className="bg-muted p-4 rounded-lg text-sm text-left">
            <p><strong>Debug Info:</strong></p>
            <p>Student ID: {user?.uid}</p>
            <p>Residence ID: {student?.residenceId || 'Not set'}</p>
            <p>Student Email: {student?.email}</p>
          </div>
        </div>
      </div>
    );
  }

  // Regulation not accepted - show blocking dialog
  if (!acceptanceCheck.hasAccepted) {
    const contract = acceptanceCheck.contract;

    // No rental contract file uploaded yet — block portal access with an
    // informational dialog. The student cannot complete acknowledgement
    // without seeing the contract PDF.
    if (!contract?.contractFileUrl || !contract.contractFilePath) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-2">Rental Contract Pending</h2>
            <p className="text-muted-foreground">
              Your rental contract is being prepared by the residence team.
              Please check back shortly — once it's available you'll be asked
              to review and acknowledge it together with the residence
              regulations.
            </p>
          </div>
        </div>
      );
    }

    const handleAccept = async () => {
      try {
        await recordAcceptance.mutateAsync({
          studentId: user.uid,
          regulationId: acceptanceCheck.regulation.id,
          regulationVersion: acceptanceCheck.regulation.version,
          residenceId: student.residenceId,
          contract: {
            contractId: contract.id,
            contractFilePath: contract.contractFilePath,
          },
        });

        toast({
          title: "Acceptance Recorded",
          description: acceptanceCheck.isReAcceptance
            ? "Thank you for re-acknowledging the regulations and rental contract."
            : "Welcome to your student portal!",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to record acceptance. Please try again.",
          variant: "destructive",
        });
      }
    };

    return (
      <RegulationAcceptanceDialog
        regulation={acceptanceCheck.regulation}
        contract={contract}
        onAccept={handleAccept}
        isAccepting={recordAcceptance.isPending}
        previousAcceptance={acceptanceCheck.previousAcceptance}
        isReAcceptance={acceptanceCheck.isReAcceptance}
      />
    );
  }

  // Personal details missing from the booking - show blocking dialog.
  // Last gate on purpose: the student should get past password and regulation
  // before being asked for admin data. Skipped while the booking is still
  // loading, or when no guest entry could be matched, so a lookup failure never
  // locks anyone out of the portal.
  if (
    !guestDetailsLoading &&
    myGuestDetails &&
    !isGuestInfoComplete(myGuestDetails.guest)
  ) {
    const handleCompleteDetails = async (guest: GuestInfo) => {
      try {
        await updateMyGuestDetails.mutateAsync(guest);
        toast({
          title: "Details Saved",
          description: "Thank you. Your information is now up to date.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error ? error.message : "Failed to save your details",
          variant: "destructive",
        });
        throw error; // Re-throw so the dialog keeps the form open
      }
    };

    return (
      <CompleteDetailsDialog
        guest={myGuestDetails.guest}
        onSubmit={handleCompleteDetails}
        isSaving={updateMyGuestDetails.isPending}
      />
    );
  }

  // All checks passed - render portal
  return <>{children}</>;
};
